import * as React from 'react'
import { AdvancedAlert, EvalData } from './AlertsListPage/model'
import { AlertResourceDeploymentType } from '../../common/resource/model'
import { AlertSeverity } from '@mulesoft/monitoring-center-components/lib/components/Selector/SeveritySelector/model'
// tslint:disable-next-line:max-line-length
import BaseBasicAlertCondition from './AlertModals/BasicAlertModal/BasicAlertModalBody/BasicAlertModalConfigurations/BasicAlertCondition/BaseBasicAlertCondition/BaseBasicAlertCondition'
// tslint:disable-next-line:max-line-length
import APIPolicyViolationConditionContainer from './AlertModals/BasicAlertModal/BasicAlertModalBody/BasicAlertModalConfigurations/BasicAlertCondition/APIPolicyViolationCondition/APIPolicyViolationConditionContainer'
// tslint:disable-next-line:max-line-length
import APIResponseCodesCondition from './AlertModals/BasicAlertModal/BasicAlertModalBody/BasicAlertModalConfigurations/BasicAlertCondition/APIResponseCodesCondition/APIResponseCodesCondition'
import { BasicSelectorOption } from '@mulesoft/monitoring-center-components/lib/components/Selector/BasicSelector/BasicSelector'

export type AlertsFeatureFlags = {
  basic: boolean
  complex?: boolean
  dynamic?: boolean
}

export type AlertsLimitExceeded = AlertsFeatureFlags

export type Alert = AdvancedAlert | BasicAlert

export type AlertsState = {
  isBasicAlertsEnabled: boolean
  isAPIBasicAlertsEnabled: boolean
  isBasicAlertsLimitExceeded: boolean
}

export type BasicAlert = {
  alertId?: string,
  alertType: 'basic',
  alertName: string,
  organizationId?: string,
  masterOrganizationId?: string,
  newStateDate?: string,
  alertState?: string,
  resource: AlertResource,
  alertViolationInterval: number,
  aggregationInterval: number,
  aggregationFunction: string,
  metricType: BasicAlertsMetricType,
  properties: AlertProperty[],
  alertNotifications: AlertNotification[],
  isLoading?: boolean,
  evalData?: EvalData,
  isNew?: boolean,
  severity: AlertSeverity | null
}

export enum AlertResourceType {
  Application = 'application',
  Server = 'server',
  ServerGroup = 'server_group',
  Cluster = 'cluster',
  API = 'api',
}

export type AlertResource = {
  type: AlertResourceType,
  organizationId: string,
  environmentId: string,
  deploymentType?: AlertResourceDeploymentType,
  clusterId?: string,
  appId?: string
  apiId?: string
  apiVersionId?: string
  policyId?: string
  responseCodes?: string[]
}

export enum BasicAlertsMetricType {
  MessageCount = 'message_count',
  MessageErrorCount = 'message_error_count',
  MessageResponseTime = 'message_response_time',
  CpuUtilization = 'cpu_utilization',
  MemoryUtilization = 'memory_utilization',
  ThreadCount = 'thread_count',
  APIRequestCount = 'api_request_count',
  APIResponseTime = 'api_response_time',
  APIResponseCodes = 'api_response_code_count',
  APIPolicyViolation = 'api_policy_violation_count'
}

export type AlertProperty = {
  name: string,
  value: string
}

export type AlertNotificationType = {
  type: string
}

export type EmailNotification = AlertNotificationType & {
  recipients: string[],
  subject: string,
  message: string
}

export type AlertNotification =
  | EmailNotification

export type BasicAlertConditionFields = {
  operator?: string
  threshold?: string
  window?: string
  orgId?: string
  envId?: string
  appId?: string
  apiId?: string
  policyId?: string
  responseCodes?: string
  editDisabled?: boolean
}

export type BasicAlertMetricsMap = {
  [key in BasicAlertsMetricType]: BasicAlertMetricsMapValue
}

export type BasicAlertMetricsMapValue = {
  aggregation: string
  isBasicAlertValid: (basicAlert: BasicAlert) => boolean
  condition: (props: BasicAlertConditionProps) => React.ReactNode
  getThresholdProperty: (threshold: string) => AlertProperty
  getValidMinThreshold: (operator: string, threshold: string | undefined) => string
}

export type BasicAlertConditionProps = {
  getValidMinThreshold: (operator: string, threshold: string | undefined) => string
  onChange: (fields: BasicAlertConditionFields) => void
  fields: BasicAlertConditionFields
}

export const basicAlertAPIMetricTypes = [
  BasicAlertsMetricType.APIRequestCount,
  BasicAlertsMetricType.APIResponseTime,
  BasicAlertsMetricType.APIPolicyViolation,
  BasicAlertsMetricType.APIResponseCodes
]

export const isBasicAlert = (alert: any): alert is BasicAlert => {
  return (alert as BasicAlert).alertType === 'basic'
}

export const getAlertProperty = (alert: BasicAlert, propName: string) => {
  return alert.properties.find(prop => prop.name === propName)
}

export const getAlertNotification = (alert: BasicAlert, type: string): AlertNotification | undefined => {
  return alert.alertNotifications && alert.alertNotifications.find(notification => notification.type === type)
}

export const isAPIMetricType = (metricType: BasicAlertsMetricType): boolean => {
  return basicAlertAPIMetricTypes.indexOf(metricType) >= 0;
}

export const getDefaultAlertResource = (sourceType: AlertResourceType, policyId?: string, responseCodes?: string[]): AlertResource => {
  return {
    type: sourceType,
    organizationId: '',
    environmentId: '',
    deploymentType: AlertResourceDeploymentType.CLOUDHUB,
    clusterId: '',
    policyId: policyId,
    responseCodes: responseCodes
  }
}

export const basicAlertsEvaluationWindowOptions = [
  {value: 5, label: '5'},
  {value: 10, label: '10'},
  {value: 15, label: '15'},
  {value: 20, label: '20'}
]

export const basicAlertsOperators = [
  {value: 'above', label: 'Above'},
  {value: 'aboveOrEqual', label: 'Above or equal to'},
  {value: 'equal', label: 'Equal to'},
  {value: 'belowOrEqual', label: 'Below or equal to'},
  {value: 'below', label: 'Below'}
]

export const defaultResponseCodeOptions: BasicSelectorOption[] = [
  { value: '301', label: '301' },
  { value: '400', label: '400' },
  { value: '401', label: '401' },
  { value: '403', label: '403' },
  { value: '404', label: '404' },
  { value: '405', label: '405' },
  { value: '408', label: '408' },
  { value: '409', label: '409' },
  { value: '410', label: '410' },
  { value: '411', label: '411' },
  { value: '412', label: '412' },
  { value: '413', label: '413' },
  { value: '414', label: '414' },
  { value: '415', label: '415' },
  { value: '416', label: '416' },
  { value: '417', label: '417' },
  { value: '420', label: '420' },
  { value: '422', label: '422' },
  { value: '429', label: '429' },
  { value: '500', label: '500' },
  { value: '502', label: '502' },
  { value: '503', label: '503' },
  { value: '504', label: '504' },
  { value: '505', label: '505' },
  { value: '510', label: '510' },
  { value: '511', label: '511' }
]

export const areConditionFieldsEqual = (f1: BasicAlertConditionFields, f2: BasicAlertConditionFields) => {
  return f1.window === f2.window &&
         f1.threshold === f2.threshold &&
         f1.operator === f2.operator &&
         f1.policyId === f2.policyId &&
         f1.orgId === f2.orgId &&
         f1.envId === f2.envId &&
         f1.appId === f2.appId &&
         f1.apiId === f2.apiId &&
         f1.responseCodes === f2.responseCodes
}

export const setAlertCondition = (alert: BasicAlert, fields: BasicAlertConditionFields): BasicAlert => {
  const properties = alert.properties.filter(prop => prop.name !== 'operator' && prop.name !== 'threshold')
  const operatorProperty: AlertProperty = {name: 'operator', value: fields.operator || ''}
  const metric = alert.metricType
  const threshold = basicAlertMetricsMap[metric].getValidMinThreshold(operatorProperty.value, fields.threshold)
  const thresholdProperty: AlertProperty = basicAlertMetricsMap[metric].getThresholdProperty(threshold)
  const window = parseInt(fields.window || '5')
  let result = {...alert, properties: properties.concat(operatorProperty).concat(thresholdProperty), alertViolationInterval: window}

  if (metric === BasicAlertsMetricType.APIPolicyViolation) {
    result = {...result, resource: {...alert.resource, policyId: fields.policyId, responseCodes: undefined}}
  } else if (metric === BasicAlertsMetricType.APIResponseCodes) {
    const responseCodes = fields.responseCodes ? fields.responseCodes.split(',') : undefined
    result = {...result, resource: {...alert.resource, responseCodes: responseCodes, policyId: undefined}}
  } else {
    result = {...result, resource: {...alert.resource, responseCodes: undefined, policyId: undefined}}
  }

  return result
}

const basicThresholdProperty = (threshold: string): AlertProperty => {
  if ((!!threshold && !(threshold.endsWith('.') || threshold.endsWith(','))) && Number(threshold) < 0) {
    return {name: 'threshold', value: '0'}
  }

  return {name: 'threshold', value: threshold}
}

const percentageThresholdProperty = (threshold: string): AlertProperty => {
  if (!!threshold && !(threshold.endsWith('.') || threshold.endsWith(','))) {
    const thresholdAsNumber = Number(threshold)
    if (thresholdAsNumber < 0) {
      return {name: 'threshold', value: '0'}
    } else if (thresholdAsNumber > 100) {
      return {name: 'threshold', value: '1'}
    } else {
      return {name: 'threshold', value: (thresholdAsNumber / 100).toString()}
    }
  }

  return {name: 'threshold', value: threshold}
}

export const getDefaultMetricThreshold = (operator: string, threshold: string | undefined): string => {
  if (isThresholdLowerThan(threshold, 0)) return '0'
  else return threshold || '0'
}

export const getDefaultAPIMetricThreshold = (operator: string, threshold: string | undefined): string => {
  if (operator === 'below' && isThresholdLowerThan(threshold, 2)) {
    return '2'
  } else if (isThresholdLowerThan(threshold, 1)) {
    return '1'
  } else {
    return threshold || '1'
  }
}

const basicAlertOrgAndEnvValidation = (basicAlert: BasicAlert): boolean => {
  return !!basicAlert.resource.organizationId && !!basicAlert.resource.environmentId
}

const basicAlertApplicationBasicValidation = (basicAlert: BasicAlert): boolean => {
  return basicAlertOrgAndEnvValidation(basicAlert) && basicAlert.resource.type === 'application' && !!basicAlert.resource.appId
}

const basicAlertJVMBasicValidation = (basicAlert: BasicAlert): boolean => {
  switch (basicAlert.resource.type) {
    case 'server':
    case 'server_group':
    case 'cluster':
      return basicAlertOrgAndEnvValidation(basicAlert) && !!basicAlert.resource.clusterId
    default:
      if (basicAlert.resource.deploymentType === AlertResourceDeploymentType.HYBRID) {
        return basicAlertApplicationBasicValidation(basicAlert) && !!basicAlert.resource.clusterId
      } else {
        return basicAlertApplicationBasicValidation(basicAlert)
      }
  }
}

const basicAlertAPIBasicValidation = (basicAlert: BasicAlert): boolean => {
  return basicAlertOrgAndEnvValidation(basicAlert) && basicAlert.resource.type === 'api' && !!basicAlert.resource.apiVersionId
}

const basicAlertAPIPolicyValidation = (basicAlert: BasicAlert): boolean => {
  return basicAlertAPIBasicValidation(basicAlert) && !!basicAlert.resource.policyId
}

const basicAlertResponseCodesValidation = (basicAlert: BasicAlert): boolean => {
  return basicAlertAPIBasicValidation(basicAlert) && !!basicAlert.resource.responseCodes
}

const basicMetricMapValue = (metric: string, aggregation: string, unit?: string) => ({
  aggregation: aggregation,
  getThresholdProperty: basicThresholdProperty,
  getValidMinThreshold: getDefaultMetricThreshold,
  isBasicAlertValid: basicAlertApplicationBasicValidation,
  condition: (props: BasicAlertConditionProps) => {
    const conditionProps = {...props, metric, unit}
    return (<BaseBasicAlertCondition {...conditionProps}/>)
  }
})

const threadCountMetricMapValue = (metric: string, aggregation: string, unit?: string) => ({
  ...basicMetricMapValue(metric, aggregation, unit),
  isBasicAlertValid: basicAlertJVMBasicValidation
})

const percentageThresholdMetricMapValue = (metric: string, aggregation: string, unit?: string) => ({
  ...basicMetricMapValue(metric, aggregation, unit),
  getThresholdProperty: percentageThresholdProperty,
  isBasicAlertValid: basicAlertJVMBasicValidation,
})

const apiMetricMapValue = (metric: string, aggregation: string, unit?: string) => ({
  ...basicMetricMapValue(metric, aggregation, unit),
  isBasicAlertValid: basicAlertAPIBasicValidation,
})

const apiRequestCountMapValue = {
  ...apiMetricMapValue('total request count', 'sum'),
  getValidMinThreshold: getDefaultAPIMetricThreshold
}

const apiPolicyViolationMapValue = {
  aggregation: 'sum',
  getThresholdProperty: basicThresholdProperty,
  getValidMinThreshold: getDefaultAPIMetricThreshold,
  isBasicAlertValid: basicAlertAPIPolicyValidation,
  condition: (props: BasicAlertConditionProps) => {
    const conditionProps = {...props, metric: 'policy violation'}
    return (<APIPolicyViolationConditionContainer {...conditionProps}/>)
  }
}

const apiResponseCodesMapValue = {
  aggregation: 'sum',
  getThresholdProperty: basicThresholdProperty,
  getValidMinThreshold: getDefaultAPIMetricThreshold,
  isBasicAlertValid: basicAlertResponseCodesValidation,
  condition: (props: BasicAlertConditionProps & {apiResponseCodesLimit: number}) => {
    const conditionProps = {...props, metric: 'number of occurrences'}
    return (<APIResponseCodesCondition {...conditionProps}/>)
  }
}

export const basicAlertMetricsMap: BasicAlertMetricsMap = {
  [BasicAlertsMetricType.MessageCount]: basicMetricMapValue('message count', 'sum'),
  [BasicAlertsMetricType.MessageErrorCount]: basicMetricMapValue('message error count', 'sum'),
  [BasicAlertsMetricType.MessageResponseTime]: basicMetricMapValue('message response time', 'mean', 'ms'),
  [BasicAlertsMetricType.CpuUtilization]: percentageThresholdMetricMapValue('cpu utilization', 'mean', '%'),
  [BasicAlertsMetricType.MemoryUtilization]: percentageThresholdMetricMapValue('memory utilization', 'mean', '%'),
  [BasicAlertsMetricType.ThreadCount]: threadCountMetricMapValue('thread count', 'mean'),
  [BasicAlertsMetricType.APIRequestCount]: apiRequestCountMapValue,
  [BasicAlertsMetricType.APIResponseTime]: apiMetricMapValue('average response time', 'mean', 'ms'),
  [BasicAlertsMetricType.APIPolicyViolation]: apiPolicyViolationMapValue,
  [BasicAlertsMetricType.APIResponseCodes]: apiResponseCodesMapValue,
}

export const getMetricThreshold = (metricType: string, thresholdProperty: AlertProperty): string => {
  if (!thresholdProperty.value || (thresholdProperty.value.endsWith('.') || thresholdProperty.value.endsWith(','))) {
    return thresholdProperty.value || '0'
  } else {
    const isPercentageMetric = metricType === BasicAlertsMetricType.CpuUtilization || metricType === BasicAlertsMetricType.MemoryUtilization
    const thresholdValue = thresholdProperty.value.replace(',', '.')
    const numberValue = isPercentageMetric ? Number(thresholdValue) * 100 : Number(thresholdValue)
    return Math.round(numberValue).toString()
  }
}

const isThresholdLowerThan = (threshold: string | undefined, x: number): boolean => {
  return (!!threshold && !(threshold.endsWith('.') || threshold.endsWith(','))) && Number(threshold) < x
}
